"use client";

import { useMemo } from "react";

import {
  getNetCashBalance,
  getPortfolioAllocationBreakdown,
  getPortfolioAllocationPercent,
} from "@lattice/core-lib/utils/portfolio-allocation";
import {
  ALL_PORTFOLIO_ACCOUNTS_ID,
  buildPortfolioAccountOptions,
  getPortfolioAccountTotalValue,
  getPortfolioAssetValue,
  isPortfolioCashLikeAsset,
  resolvePortfolioAccountSelection,
} from "@lattice/shared-frontend/charts";

import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import type {
  T_PortfolioAccount,
  T_PortfolioAsset,
  T_PortfolioHistory,
  T_PortfolioHomeChartPoint,
  T_PortfolioHomeData,
  T_PortfolioHomeHolding,
  T_PortfolioHomeInterval,
  T_PortfolioHomeViewState,
} from "./types";

const FALLBACK_WINDOW_MS = 24 * 60 * 60 * 1000;

function getPeriodLabel(interval: T_PortfolioHomeInterval): string {
  switch (interval) {
    case "1D":
      return "Today";
    case "MAX":
      return "All time";
    case "1W":
    case "1M":
    case "3M":
    case "YTD":
    case "1Y":
      return interval;
  }
}

function getPortfolioTimeframeStartDate(
  interval: T_PortfolioHomeInterval,
  anchor: Date,
): Date {
  const date = new Date(anchor);
  switch (interval) {
    case "1D":
      date.setDate(date.getDate() - 1);
      return date;
    case "1W":
      date.setDate(date.getDate() - 7);
      return date;
    case "1M":
      date.setMonth(date.getMonth() - 1);
      return date;
    case "3M":
      date.setMonth(date.getMonth() - 3);
      return date;
    case "YTD":
      return new Date(anchor.getFullYear(), 0, 1);
    case "1Y":
      date.setFullYear(date.getFullYear() - 1);
      return date;
    case "MAX":
      return new Date(0);
  }
}

function mergeNumber(
  first: number | null | undefined,
  second: number | null | undefined,
): number | null {
  if (first === null || first === undefined) return second ?? null;
  if (second === null || second === undefined) return first;
  return first + second;
}

function getCostBasis(asset: T_PortfolioAsset): number | null {
  if (asset.cost_basis !== null && asset.cost_basis !== undefined) {
    return asset.cost_basis;
  }
  if (asset.average_purchase_price !== null) {
    return asset.average_purchase_price * asset.units;
  }
  return null;
}

function buildAggregatedAssets(
  accounts: T_PortfolioAccount[],
): T_PortfolioAsset[] {
  const assetsBySymbol = new Map<string, T_PortfolioAsset>();
  for (const account of accounts) {
    for (const asset of account.assets) {
      if (isPortfolioCashLikeAsset(asset)) continue;
      const symbol = asset.symbol.trim().toUpperCase();
      if (!symbol) continue;
      const existing = assetsBySymbol.get(symbol);
      if (!existing) {
        assetsBySymbol.set(symbol, { ...asset, symbol });
        continue;
      }

      const units = existing.units + asset.units;
      const costBasis = mergeNumber(
        getCostBasis(existing),
        getCostBasis(asset),
      );
      assetsBySymbol.set(symbol, {
        ...existing,
        average_purchase_price:
          costBasis !== null && units > 0 ? costBasis / units : null,
        cost_basis: costBasis,
        total_value: mergeNumber(existing.total_value, asset.total_value),
        units,
      });
    }
  }
  return [...assetsBySymbol.values()].sort(
    (first, second) =>
      getPortfolioAssetValue(second) - getPortfolioAssetValue(first),
  );
}

function buildHistoryPoints(
  history: T_PortfolioHistory,
  selectedAccountId: string,
): T_PortfolioHomeChartPoint[] {
  const accountId =
    selectedAccountId === ALL_PORTFOLIO_ACCOUNTS_ID ? null : selectedAccountId;
  return history
    .flatMap((entry) => {
      const accounts =
        accountId === null
          ? entry.accounts
          : entry.accounts.filter(
              (account) => account.snaptrade_account_cuid === accountId,
            );
      if (accounts.length === 0) return [];
      return [
        {
          timestamp: entry.date.getTime(),
          value: accounts.reduce(
            (sum, account) => sum + account.total_value,
            0,
          ),
        },
      ];
    })
    .sort((first, second) => first.timestamp - second.timestamp);
}

function windowHistory(
  points: T_PortfolioHomeChartPoint[],
  interval: T_PortfolioHomeInterval,
): T_PortfolioHomeChartPoint[] {
  if (points.length < 2) return points;
  const anchor = points[points.length - 2] ?? points[points.length - 1];
  if (!anchor) return points;
  const start = getPortfolioTimeframeStartDate(
    interval,
    new Date(anchor.timestamp),
  ).getTime();
  return points.filter((point) => point.timestamp >= start);
}

function getPeriodChange(points: T_PortfolioHomeChartPoint[]) {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) {
    return { dollar: 0, percent: 0 };
  }
  const dollar = last.value - first.value;
  return {
    dollar,
    percent: first.value > 0 ? (dollar / first.value) * 100 : 0,
  };
}

function buildHoldings(
  accounts: T_PortfolioAccount[],
  cashBalance: number,
  liabilityBalance: number,
): T_PortfolioHomeHolding[] {
  const assets = buildAggregatedAssets(accounts);
  const holdingsValue = assets.reduce(
    (sum, asset) => sum + getPortfolioAssetValue(asset),
    0,
  );
  const { allocationBase } = getPortfolioAllocationBreakdown({
    cashBalance,
    holdingsValue,
    liabilityBalance,
  });
  return assets.map((asset) => {
    const totalValue = getPortfolioAssetValue(asset);
    return {
      dailyPercentChange: null,
      logoImage: asset.stock_info?.image ?? null,
      name: asset.stock_info?.name ?? null,
      symbol: asset.symbol,
      totalValue,
      units: asset.units,
      weightPercent: getPortfolioAllocationPercent({
        allocationBase,
        value: totalValue,
      }),
    };
  });
}

function buildPortfolioHomeData(args: {
  accounts: T_PortfolioAccount[];
  history: T_PortfolioHistory;
  interval: T_PortfolioHomeInterval;
  now?: number;
  requestedAccountId: string;
  summary: {
    cash_balance: number;
    liability_balance: number;
    total_portfolio_value: number;
  };
}): T_PortfolioHomeData {
  const selection = resolvePortfolioAccountSelection(
    args.accounts,
    args.requestedAccountId,
  );
  const cashBalance = selection.isViewingAllAccounts
    ? args.summary.cash_balance
    : selection.selectedAccounts.reduce(
        (sum, account) => sum + account.cash_balance,
        0,
      );
  const liabilityBalance = selection.isViewingAllAccounts
    ? args.summary.liability_balance
    : selection.selectedAccounts.reduce(
        (sum, account) => sum + account.liability_balance,
        0,
      );
  const totalValue = selection.isViewingAllAccounts
    ? args.summary.total_portfolio_value +
      getNetCashBalance({ cashBalance, liabilityBalance })
    : selection.selectedAccounts.reduce(
        (sum, account) => sum + getPortfolioAccountTotalValue(account),
        0,
      );
  const allHistoryPoints = buildHistoryPoints(
    args.history,
    selection.selectedAccountId,
  );
  const persistedPoints = windowHistory(allHistoryPoints, args.interval);
  const dayPoints = windowHistory(allHistoryPoints, "1D");
  const hasPersistedHistory = persistedPoints.length >= 2;
  const now = args.now ?? Date.now();
  const chartPoints = hasPersistedHistory
    ? persistedPoints
    : totalValue > 0
      ? [
          { timestamp: now - FALLBACK_WINDOW_MS, value: totalValue },
          { timestamp: now, value: totalValue },
        ]
      : [];
  const periodChange = getPeriodChange(persistedPoints);
  const dayChange = getPeriodChange(dayPoints);

  return {
    accountOptions: buildPortfolioAccountOptions(args.accounts),
    chartPoints,
    dayDirection: dayChange.percent < 0 ? "down" : "up",
    dayPercentChange: dayChange.percent,
    hasPersistedHistory,
    holdings: buildHoldings(
      selection.selectedAccounts,
      cashBalance,
      liabilityBalance,
    ),
    isPositive: periodChange.dollar >= 0,
    periodDollarChange: periodChange.dollar,
    periodLabel: getPeriodLabel(args.interval),
    periodPercentChange: periodChange.percent,
    selectedAccountId: selection.selectedAccountId,
    totalValue,
  };
}

export function usePortfolioHomeData(
  interval: T_PortfolioHomeInterval,
  requestedAccountId: string,
): T_PortfolioHomeViewState {
  const accountsQuery = trpcApi.snaptrade.getAccounts.useQuery(undefined, {
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 4 * 60 * 60 * 1000,
  });
  const historyQuery = trpcApi.snaptrade.getAccountValueHistory.useQuery(
    undefined,
    {
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 4 * 60 * 60 * 1000,
    },
  );

  return useMemo(() => {
    if (accountsQuery.isPending || historyQuery.isPending) {
      return { status: "loading" };
    }
    if (accountsQuery.error || historyQuery.error) {
      return {
        message: "Portfolio data could not be loaded. Please try again.",
        status: "error",
      };
    }
    if (!accountsQuery.data || accountsQuery.data.accounts.length === 0) {
      return { status: "empty" };
    }

    return {
      data: buildPortfolioHomeData({
        accounts: accountsQuery.data.accounts,
        history: historyQuery.data ?? [],
        interval,
        requestedAccountId,
        summary: accountsQuery.data.summary,
      }),
      status: "success",
    };
  }, [
    accountsQuery.data,
    accountsQuery.error,
    accountsQuery.isPending,
    historyQuery.data,
    historyQuery.error,
    historyQuery.isPending,
    interval,
    requestedAccountId,
  ]);
}
